import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from './cn';

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Visible label text. */
  label: string;
  /** Optional helper text shown below the input when there is no error. */
  helperText?: ReactNode;
  /** Validation error. When set, swaps helper text and applies error styling. */
  error?: string | undefined;
  /** Class name applied to the wrapper. */
  wrapperClassName?: string;
}

/**
 * Accessible single-line text input with an associated label and helper/error
 * text. Refs forwarded so callers can `.focus()` and trigger HTML5 validation.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, helperText, error, wrapperClassName, id, className, required, ...rest }, ref) => {
    const reactId = useId();
    const inputId = id ?? reactId;
    const helperId = `${inputId}-help`;

    return (
      <div className={cn('flex flex-col gap-1.5', wrapperClassName)}>
        <label htmlFor={inputId} className="text-caption font-medium text-ink">
          {label}
          {required ? <span className="ml-0.5 text-[#dd5b00]">*</span> : null}
        </label>
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={helperText || error ? helperId : undefined}
          required={required}
          className={cn(
            'h-10 rounded-input border bg-white px-3 text-body text-ink',
            'transition-colors duration-150 ease-notion',
            'focus:border-accent-focus',
            error ? 'border-[#dd5b00]' : 'border-warm-200',
            className,
          )}
          {...rest}
        />
        {(error ?? helperText) ? (
          <p
            id={helperId}
            className={cn('text-[12px] leading-tight', error ? 'text-[#dd5b00]' : 'text-warm-500')}
          >
            {error ?? helperText}
          </p>
        ) : null}
      </div>
    );
  },
);
TextField.displayName = 'TextField';
